import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Save, Tag, Plus, Sparkles, AlertCircle } from 'lucide-react';

const EditPromptModal = ({ isOpen, onClose, prompt, onUpdate }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    template: '',
    tags: []
  });
  const [tagInput, setTagInput] = useState('');
  const [variables, setVariables] = useState([]);
  const [saveVersion, setSaveVersion] = useState(true);
  const [versionNotes, setVersionNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (prompt) {
      setFormData({
        name: prompt.name || '',
        description: prompt.description || '',
        category: prompt.category || 'Marketing',
        template: prompt.template || '',
        tags: prompt.tags || []
      });
      extractVariables(prompt.template || '');
    }
  }, [prompt]);

  const extractVariables = (template) => {
    const regex = /\{\{(\w+)\}\}/g;
    const matches = [...template.matchAll(regex)];
    const uniqueVars = [...new Set(matches.map(m => m[1]))];
    setVariables(uniqueVars);
  };

  const handleTemplateChange = (value) => {
    setFormData({ ...formData, template: value });
    extractVariables(value);
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tagInput.trim()]
      });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.template.trim()) {
      alert('Le nom et le template sont requis');
      return;
    }

    setSaving(true);

    try {
      // Save version if requested
      if (saveVersion) {
        const versionResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/ai-studio/prompts-library/${prompt.id}/version`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ notes: versionNotes || 'Version sauvegardée avant modification' })
          }
        );

        if (!versionResponse.ok) {
          console.warn('Failed to save version, continuing with update');
        }
      }

      // Update prompt
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai-studio/prompts/${prompt.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            ...formData,
            variables,
            workspace_id: prompt.workspace_id
          })
        }
      );

      if (!response.ok) {
        throw new Error('Erreur lors de la mise à jour');
      }

      const data = await response.json();
      
      if (data.id || data.success) {
        onUpdate && onUpdate();
        onClose();
      }
    } catch (error) {
      console.error('Error updating prompt:', error);
      alert('Erreur lors de la mise à jour du prompt');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !prompt) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="backdrop-blur-xl bg-slate-900/95 border border-white/10 rounded-2xl p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <Sparkles className="text-cyan-400" size={28} />
              Modifier le Prompt
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="text-slate-400" size={24} />
            </button>
          </div>

          {/* Version Info */}
          <div className="mb-6 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg flex items-start gap-2">
            <AlertCircle className="text-blue-400 flex-shrink-0 mt-0.5" size={16} />
            <div className="text-xs text-blue-400">
              <p className="font-medium mb-1">Version actuelle : v{prompt.version || 1}</p>
              <p className="text-blue-300">Une copie de la version actuelle sera sauvegardée avant la modification</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Nom du Prompt *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 bg-slate-800/50 border border-white/10 rounded-xl text-white focus:outline-none focus:border-cyan-500 transition-all"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="3"
                className="w-full px-4 py-3 bg-slate-800/50 border border-white/10 rounded-xl text-white resize-none focus:outline-none focus:border-cyan-500 transition-all"
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Catégorie
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-3 bg-slate-800/50 border border-white/10 rounded-xl text-white focus:outline-none focus:border-cyan-500 transition-all"
              >
                <option value="Marketing">📣 Marketing</option>
                <option value="E-commerce">🛍️ E-commerce</option>
                <option value="Social Media">📱 Social Media</option>
                <option value="Email">✉️ Email</option>
                <option value="Blog">📝 Blog</option>
                <option value="Ads">🎯 Ads</option>
                <option value="SEO">🔍 SEO</option>
                <option value="Custom">⚙️ Custom</option>
              </select>
            </div>

            {/* Template */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Template du Prompt *
              </label>
              <textarea
                value={formData.template}
                onChange={(e) => handleTemplateChange(e.target.value)}
                rows="6"
                className="w-full px-4 py-3 bg-slate-800/50 border border-white/10 rounded-xl text-white font-mono text-sm resize-none focus:outline-none focus:border-cyan-500 transition-all"
                required
              />
              
              {variables.length > 0 && (
                <div className="mt-3 p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                  <p className="text-xs text-cyan-400 mb-2">✓ Variables : {variables.length}</p>
                  <div className="flex flex-wrap gap-2">
                    {variables.map((variable, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-cyan-500/20 text-cyan-300 rounded-md text-xs font-mono"
                      >
                        {`{{${variable}}}`}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Tags
              </label>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  placeholder="Ajouter un tag..."
                  className="flex-1 px-4 py-2 bg-slate-800/50 border border-white/10 rounded-lg text-white focus:outline-none focus:border-cyan-500 transition-all"
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg border border-cyan-500/30 hover:bg-cyan-500/30 transition-all"
                >
                  <Plus size={20} />
                </button>
              </div>
              
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-slate-800 text-slate-300 rounded-lg border border-white/10 flex items-center gap-2 group"
                    >
                      <Tag size={12} />
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Version Options */}
            <div className="border-t border-white/10 pt-6">
              <label className="flex items-center gap-3 mb-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={saveVersion}
                  onChange={(e) => setSaveVersion(e.target.checked)}
                  className="w-5 h-5 rounded border-2 border-cyan-500 bg-slate-800 checked:bg-cyan-500 cursor-pointer"
                />
                <div>
                  <span className="text-white font-medium">Sauvegarder la version actuelle</span>
                  <p className="text-xs text-slate-400">Créer un snapshot avant modification (recommandé)</p>
                </div>
              </label>

              {saveVersion && (
                <input
                  type="text"
                  value={versionNotes}
                  onChange={(e) => setVersionNotes(e.target.value)}
                  placeholder="Notes de version (optionnel)..."
                  className="w-full px-4 py-2 bg-slate-800/50 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500 transition-all"
                />
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="flex-1 px-6 py-3 bg-slate-800 text-slate-300 rounded-xl border border-white/10 hover:bg-slate-700 transition-all font-semibold disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-cyan-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {saving ? (
                  'Sauvegarde...'
                ) : (
                  <>
                    <Save size={20} />
                    Enregistrer
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EditPromptModal;
